import { useEffect, useState } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { TodoListLinkResponse } from '@/lib/api';
import { useTodoSections } from '../hooks';
import { SectionSelect } from './SectionSelect';

interface MoveToListDialogProps {
  open: boolean;
  onClose: () => void;
  /** 'item' lands the task in a section; 'section' attaches the subtree under a parent. */
  mode: 'item' | 'section';
  holderId: string | null;
  /** All other lists in the workspace (already excludes the source list). */
  otherLists: Array<{ listId: string; name: string }>;
  /** Pinned move shortcuts (links) for the source list — shown as quick targets. */
  links: TodoListLinkResponse[];
  /** Title of the task / name of the section being moved (for the header). */
  label: string;
  /** Perform the move; sectionId is the landing section (item) or parent (section),
   * null = Unsorted / top level. */
  onMove: (targetListId: string, sectionId: string | null) => Promise<void>;
}

/**
 * Pick a destination list (plus an optional section) to re-home a task or a
 * whole section subtree into another list in the same workspace. Linked lists
 * appear as one-tap quick targets; any other list is reachable from the picker.
 * For a task, the section is where it lands; for a section, it's the parent to
 * attach under. Owner/admin only — the caller gates the affordance.
 */
export function MoveToListDialog({
  open,
  onClose,
  mode,
  holderId,
  otherLists,
  links,
  label,
  onMove,
}: MoveToListDialogProps) {
  const [targetListId, setTargetListId] = useState<string>('');
  // 'none' = Unsorted (item) / top level (section).
  const [sectionValue, setSectionValue] = useState<string>('none');
  const [saving, setSaving] = useState(false);

  const nameById = new Map(otherLists.map((l) => [l.listId, l.name]));
  // Linked targets that still resolve to a real workspace list, for quick-pick.
  const linkedTargets = links
    .map((l) => ({ link: l, name: nameById.get(l.targetListId) }))
    .filter((x): x is { link: TodoListLinkResponse; name: string } => !!x.name);
  const linkedTargetIds = new Set(linkedTargets.map((x) => x.link.targetListId));

  // The first linked list is pre-selected on open so a task can be moved in two
  // clicks (open → Move). Primitives (not the array) drive the reset effect so a
  // re-render never clobbers the user's in-dialog choice.
  const firstLinked = linkedTargets[0]?.link;
  const firstLinkedTargetId = firstLinked?.targetListId ?? '';
  const firstLinkedDefaultSection = firstLinked?.defaultTargetSectionId ?? '';

  const pickTarget = (id: string) => {
    setTargetListId(id);
    // For a task move, preselect the link's default landing section (if any).
    if (mode === 'item') {
      const link = links.find((l) => l.targetListId === id);
      setSectionValue(link?.defaultTargetSectionId ?? 'none');
    } else {
      setSectionValue('none');
    }
  };

  // Toggle a linked quick-target: clicking the selected one de-selects it (which
  // brings the List dropdown back); clicking another switches to it.
  const toggleLinked = (id: string) => {
    if (targetListId === id) {
      setTargetListId('');
      setSectionValue('none');
    } else {
      pickTarget(id);
    }
  };

  // On open: default to the first linked list (List dropdown hidden, Section
  // shown); with no links, start empty (dropdown shown, Section hidden).
  useEffect(() => {
    if (!open) return;
    if (firstLinkedTargetId) {
      setTargetListId(firstLinkedTargetId);
      setSectionValue(
        mode === 'item' && firstLinkedDefaultSection ? firstLinkedDefaultSection : 'none',
      );
    } else {
      setTargetListId('');
      setSectionValue('none');
    }
  }, [open, firstLinkedTargetId, firstLinkedDefaultSection, mode]);

  // Destination sections load only once a target is chosen.
  const { sections } = useTodoSections(holderId, targetListId);

  // Selecting a LINKED list hides the List dropdown — the quick buttons already
  // express the choice. No selection, or a non-linked one, keeps it visible.
  const isLinkedSelected = targetListId !== '' && linkedTargetIds.has(targetListId);
  const showListDropdown = !isLinkedSelected;

  const handleMove = async () => {
    if (!targetListId) return;
    setSaving(true);
    try {
      await onMove(targetListId, sectionValue === 'none' ? null : sectionValue);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const noTargets = otherLists.length === 0;
  const sectionLabel = mode === 'item' ? 'Section' : 'Under section';
  const noneLabel = mode === 'item' ? 'Unsorted' : '(Top level)';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <ArrowRightLeft className="h-4 w-4" />
            {mode === 'item' ? 'Move task to another list' : 'Move section to another list'}
          </DialogTitle>
          <DialogDescription>
            Move &ldquo;{label}&rdquo; into another list in this workspace.
            {mode === 'section' && ' Its sub-sections and tasks move with it.'}
          </DialogDescription>
        </DialogHeader>

        {noTargets ? (
          <p className="py-2 text-sm text-muted-foreground">
            There are no other lists in this workspace to move to.
          </p>
        ) : (
          <div className="space-y-4 py-1">
            {linkedTargets.length > 0 && (
              <div className="space-y-1.5">
                <Label>Linked lists</Label>
                <div className="flex flex-wrap gap-1.5">
                  {linkedTargets.map(({ link, name }) => (
                    <Button
                      key={link.linkId}
                      type="button"
                      size="sm"
                      variant={targetListId === link.targetListId ? 'default' : 'outline'}
                      className={cn('h-7')}
                      onClick={() => toggleLinked(link.targetListId)}
                    >
                      {name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {showListDropdown && (
              <div className="space-y-1.5">
                <Label htmlFor="move-target-list">List</Label>
                <Select value={targetListId} onValueChange={pickTarget}>
                  <SelectTrigger id="move-target-list" aria-label="Destination list">
                    <SelectValue placeholder="Choose a list…" />
                  </SelectTrigger>
                  <SelectContent>
                    {otherLists.map((l) => (
                      <SelectItem key={l.listId} value={l.listId}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {targetListId && (
              <div className="space-y-1.5">
                <Label htmlFor="move-target-section">{sectionLabel}</Label>
                <SectionSelect
                  id="move-target-section"
                  sections={sections}
                  value={sectionValue}
                  onChange={setSectionValue}
                  noneLabel={noneLabel}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={saving || !targetListId}>
            {saving ? 'Moving…' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
